import React, { useState, useEffect } from 'react';
import { FiX, FiShoppingCart, FiEye } from 'react-icons/fi';
import axios from 'axios';
import { API_BASE_URL } from '../../config';

export default function OrderDetailsModal({
  isOpen,
  onClose,
  order,
  onUpdateOrderDetails,
  onViewItems
}) {
  const [dispatchTrackingLink, setDispatchTrackingLink] = useState('https://www.indiapost.gov.in/_layouts/15/dop.portal.tracking/trackconsignment.aspx');
  const [dispatchTrackingId, setDispatchTrackingId] = useState('');
  const [courierPartner, setCourierPartner] = useState('India Post');
  const [estimatedDeliveryDate, setEstimatedDeliveryDate] = useState('');
  const [dispatchLocation, setDispatchLocation] = useState('Bangalore Head Post Office');
  const [dispatchRemark, setDispatchRemark] = useState('Parcel handed over to India Post.');

  const [packagingRemarks, setPackagingRemarks] = useState('');
  const [ofdLocation, setOfdLocation] = useState('');
  const [ofdRemarks, setOfdRemarks] = useState('Delivery agent assigned.');
  const [deliveredLocation, setDeliveredLocation] = useState('Customer Address');
  const [deliveredRemarks, setDeliveredRemarks] = useState('Successfully delivered.');

  const [newEventStatus, setNewEventStatus] = useState('In Transit');
  const [customEventStatus, setCustomEventStatus] = useState('');
  const [newEventLocation, setNewEventLocation] = useState('');
  const [newEventRemarks, setNewEventRemarks] = useState('');

  useEffect(() => {
    if (order) {
      setDispatchTrackingLink(order.tracking_link || 'https://www.indiapost.gov.in/_layouts/15/dop.portal.tracking/trackconsignment.aspx');
      setDispatchTrackingId(order.tracking_id || '');
      setCourierPartner(order.courier_partner || 'India Post');
      setEstimatedDeliveryDate(order.estimated_delivery_date || '');
      setOfdLocation(order.shipping_city || '');
    }
  }, [order]);

  const handleAddManualEvent = async () => {
    const statusVal = newEventStatus === 'Custom' ? customEventStatus : newEventStatus;
    if (!statusVal.trim()) {
      alert("Please provide an event status.");
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/orders/${order.id}/tracking-events`,
        {
          status: statusVal,
          location: newEventLocation,
          remarks: newEventRemarks
        },
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      setNewEventLocation('');
      setNewEventRemarks('');
      setCustomEventStatus('');
      
      // Trigger update to refresh parent
      onUpdateOrderDetails(order.id, {});
    } catch (err) {
      console.error(err);
      alert("Failed to add tracking event: " + (err.response?.data?.message || err.message));
    }
  };

  if (!isOpen || !order) return null;

  const handleWorkflowSubmit = (updates) => {
    onUpdateOrderDetails(order.id, updates);
  };

  return (
    <div className="admin-modal-overlay-bg" onClick={onClose}>
      <div className="admin-modal-form-card order-details-card animate-scale-up" onClick={(e) => e.stopPropagation()}>
        <header className="modal-form-header">
          <h2><FiShoppingCart className="modal-header-icon" /> Order details: {order.order_number}</h2>
          <button className="modal-close-x-btn" onClick={onClose} aria-label="Close modal"><FiX /></button>
        </header>

        <div className="order-details-modal-grid">
          
          {/* Left Column: Details and Workflow */}
          <div className="order-details-col-left">
            
            {/* 1. Status Info and General Details */}
            <div className="order-details-glass-panel">
              <h3>Order Overview</h3>
              <div className="status-highlight-box">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <span className="info-item-label">Status</span>
                    <span className={`order-status-badge ${order.status}`}>
                      {order.status}
                    </span>
                  </div>
                  {order.packaging_status && (
                    <div>
                      <span className="info-item-label">Packaging Progress</span>
                      <span className="order-pkg-status-badge">
                        📦 {order.packaging_status}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <div className="order-grid-2col">
                <div>
                  <span className="info-item-label">Order Date</span>
                  <p className="info-item-value">{new Date(order.created_at).toLocaleString()}</p>
                </div>
                <div>
                  <span className="info-item-label">Payment Method</span>
                  <p className="info-item-value">{order.payment_method || 'COD'}</p>
                </div>
                <div>
                  <span className="info-item-label">Payment Status</span>
                  <p className="info-item-value">{order.payment_status || 'pending'}</p>
                </div>
                <div>
                  <span className="info-item-label">Total Amount</span>
                  <p className="info-item-value" style={{ color: '#0f172a', fontWeight: 'bold' }}>₹{parseFloat(order.total_amount).toFixed(2)}</p>
                </div>
              </div>
            </div>

            {/* 2. Customer Profile */}
            <div className="order-details-glass-panel">
              <h3>Customer Details</h3>
              <div className="order-grid-2col">
                <div>
                  <span className="info-item-label">Email Address</span>
                  <p className="info-item-value">{order.user_email || 'N/A'}</p>
                </div>
                <div>
                  <span className="info-item-label">Phone Number</span>
                  <p className="info-item-value">{order.user_phone || order.shipping_phone || 'N/A'}</p>
                </div>
              </div>
            </div>

            {/* 3. Shipping & Billing Addresses */}
            <div className="order-details-glass-panel">
              <h3>Shipping Address</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <p className="info-item-value" style={{ fontWeight: 600 }}>{order.shipping_name || 'N/A'}</p>
                <p className="info-item-value">
                  {order.shipping_line1}
                  {order.shipping_line2 ? `, ${order.shipping_line2}` : ''}
                  {order.shipping_landmark ? ` (Landmark: ${order.shipping_landmark})` : ''}
                </p>
                <p className="info-item-value">
                  {order.shipping_city}, {order.shipping_state} — <strong>{order.shipping_pincode}</strong>
                </p>
                <p className="info-item-value" style={{ fontSize: '12.5px', color: '#475569' }}>
                  📞 Contact: {order.shipping_phone} {order.shipping_alt_phone ? ` | Alt: ${order.shipping_alt_phone}` : ''}
                </p>
              </div>
            </div>

            {/* 4. Tracking and Courier Info */}
            {['shipped', 'out_for_delivery', 'delivered'].includes(order.status) && (
              <div className="order-details-glass-panel">
                <h3>Courier Tracking Details</h3>
                <div className="tracking-info-preview-box">
                  <p><strong>Courier Partner:</strong> {order.courier_partner || 'India Post'}</p>
                  <p style={{ marginTop: '4px' }}><strong>Estimated Delivery:</strong> {order.estimated_delivery_date || 'Not specified'}</p>
                  <p style={{ marginTop: '4px' }}><strong>Tracking ID:</strong> <code>{order.tracking_id}</code></p>
                  {order.tracking_link && (
                    <p style={{ marginTop: '8px' }}>
                      👉 <a href={order.tracking_link} target="_blank" rel="noopener noreferrer" className="tracking-link-btn">
                        Track Shipment Online
                      </a>
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* 5. Workflow Control Actions Panel */}
            <div className="order-details-glass-panel">
              <h3>Workflow Controls</h3>
              <div className="status-workflow-actions-panel">
                
                {/* Stage 1: Approve Order (Only shown for pending orders) */}
                {order.status === 'pending' && (
                  <div className="dispatch-form-group">
                    <span className="workflow-section-title">Stage 1: Approve Order</span>
                    <div className="workflow-buttons-row">
                      <button
                        type="button"
                        className="btn-workflow-primary"
                        onClick={() => handleWorkflowSubmit({
                          status: 'processing',
                          packaging_status: 'Approved'
                        })}
                      >
                        ✓ Approve Order &amp; Start processing
                      </button>
                      <button
                        type="button"
                        className="btn-workflow-danger"
                        onClick={() => handleWorkflowSubmit({
                          status: 'cancelled'
                        })}
                      >
                        Cancel Order
                      </button>
                    </div>
                  </div>
                )}

                {/* Stage 2: Packaging (Shown for processing orders) */}
                {order.status === 'processing' && (order.packaging_status === 'Approved' || order.packaging_status === 'In Packaging' || !order.packaging_status) && (
                  <div className="dispatch-form-group">
                    <span className="workflow-section-title">Stage 2: Pack Order (Status: {order.packaging_status || 'Approved'})</span>
                    
                    <div style={{ marginBottom: '12px', marginTop: '8px' }}>
                      <label style={{ fontSize: '12px', color: '#475569', display: 'block', marginBottom: '4px' }}>Packaging Remarks / Notes</label>
                      <input 
                        type="text" 
                        value={packagingRemarks} 
                        onChange={(e) => setPackagingRemarks(e.target.value)}
                        placeholder="e.g. Quality check completed. Ready for dispatch."
                        style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                      />
                    </div>

                    <div className="workflow-buttons-row">
                      {(order.packaging_status === 'Approved' || !order.packaging_status) && (
                        <button
                          type="button"
                          className="btn-workflow-blue"
                          onClick={() => handleWorkflowSubmit({
                            packaging_status: 'In Packaging'
                          })}
                        >
                          📦 Start Packing Items
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn-workflow-primary"
                        onClick={() => {
                          handleWorkflowSubmit({
                            packaging_status: 'Packed',
                            event_remarks: packagingRemarks || 'Product packed successfully. Quality check completed. Ready for dispatch.'
                          });
                          setPackagingRemarks('');
                        }}
                      >
                        ✓ Mark as Packed
                      </button>
                      <button
                        type="button"
                        className="btn-workflow-danger"
                        onClick={() => handleWorkflowSubmit({
                          status: 'cancelled'
                        })}
                      >
                        Cancel Order
                      </button>
                    </div>
                  </div>
                )}

                {/* Stage 3: Dispatch (Shown when packaging_status is Packed) */}
                {order.status === 'processing' && order.packaging_status === 'Packed' && (
                  <div className="dispatch-form-group">
                    <span className="workflow-section-title">Stage 3: Dispatch Order via India Post</span>
                    <div className="dispatch-form-card-block" style={{ marginTop: '8px', padding: '12px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                      
                      <div className="dispatch-form-group" style={{ marginBottom: '10px' }}>
                        <label style={{ fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '3px' }}>Courier Partner</label>
                        <input
                          type="text"
                          value={courierPartner}
                          onChange={(e) => setCourierPartner(e.target.value)}
                          placeholder="e.g. India Post"
                          style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                        />
                      </div>

                      <div className="dispatch-form-group" style={{ marginBottom: '10px' }}>
                        <label style={{ fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '3px' }}>Tracking ID / Consignment Number *</label>
                        <input
                          type="text"
                          value={dispatchTrackingId}
                          onChange={(e) => setDispatchTrackingId(e.target.value)}
                          placeholder="e.g. EK123456789IN"
                          style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                        />
                      </div>

                      <div className="dispatch-form-group" style={{ marginBottom: '10px' }}>
                        <label style={{ fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '3px' }}>Tracking Link</label>
                        <input
                          type="text"
                          value={dispatchTrackingLink}
                          onChange={(e) => setDispatchTrackingLink(e.target.value)}
                          placeholder="Tracking URL"
                          style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                        />
                      </div>

                      <div className="dispatch-form-group" style={{ marginBottom: '10px' }}>
                        <label style={{ fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '3px' }}>Estimated Delivery Date *</label>
                        <input
                          type="date"
                          value={estimatedDeliveryDate}
                          onChange={(e) => setEstimatedDeliveryDate(e.target.value)}
                          style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                        />
                      </div>

                      <div className="dispatch-form-group" style={{ marginBottom: '10px' }}>
                        <label style={{ fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '3px' }}>Dispatch Post Office Location</label>
                        <input
                          type="text"
                          value={dispatchLocation}
                          onChange={(e) => setDispatchLocation(e.target.value)}
                          placeholder="e.g. Bangalore Head Post Office"
                          style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                        />
                      </div>

                      <div className="dispatch-form-group" style={{ marginBottom: '12px' }}>
                        <label style={{ fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '3px' }}>Remarks / Handover Note</label>
                        <input
                          type="text"
                          value={dispatchRemark}
                          onChange={(e) => setDispatchRemark(e.target.value)}
                          placeholder="e.g. Parcel handed over to India Post"
                          style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                        />
                      </div>

                      <div className="workflow-buttons-row">
                        <button
                          type="button"
                          className="btn-workflow-blue"
                          onClick={() => {
                            if (!dispatchTrackingId.trim()) {
                              alert("Please provide a Tracking ID / Consignment Number.");
                              return;
                            }
                            if (!estimatedDeliveryDate) {
                              alert("Please select an estimated delivery date.");
                              return;
                            }
                            handleWorkflowSubmit({
                              status: 'shipped',
                              packaging_status: 'Dispatched',
                              courier_partner: courierPartner,
                              tracking_link: dispatchTrackingLink,
                              tracking_id: dispatchTrackingId,
                              estimated_delivery_date: estimatedDeliveryDate,
                              event_location: dispatchLocation,
                              event_remarks: dispatchRemark
                            });
                          }}
                        >
                          🚀 Dispatch &amp; Handover to Post
                        </button>
                        <button
                          type="button"
                          className="btn-workflow-danger"
                          onClick={() => handleWorkflowSubmit({
                            status: 'cancelled'
                          })}
                        >
                          Cancel Order
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Stage 4: Out For Delivery (Shown for shipped orders) */}
                {order.status === 'shipped' && (
                  <div className="dispatch-form-group">
                    <span className="workflow-section-title">Stage 4: Dispatch Out For Delivery</span>
                    
                    <div style={{ background: '#f8fafc', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0', marginTop: '8px', marginBottom: '12px' }}>
                      <div className="dispatch-form-group" style={{ marginBottom: '8px' }}>
                        <label style={{ fontSize: '12px', display: 'block', marginBottom: '3px' }}>Current Location / Hub</label>
                        <input
                          type="text"
                          value={ofdLocation}
                          onChange={(e) => setOfdLocation(e.target.value)}
                          placeholder="e.g. Imphal Post Office"
                          style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                        />
                      </div>
                      <div className="dispatch-form-group" style={{ marginBottom: '8px' }}>
                        <label style={{ fontSize: '12px', display: 'block', marginBottom: '3px' }}>Out for Delivery Remark</label>
                        <input
                          type="text"
                          value={ofdRemarks}
                          onChange={(e) => setOfdRemarks(e.target.value)}
                          placeholder="e.g. Delivery agent assigned"
                          style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                        />
                      </div>
                    </div>

                    <div className="workflow-buttons-row">
                      <button
                        type="button"
                        className="btn-workflow-blue"
                        onClick={() => handleWorkflowSubmit({
                          status: 'out_for_delivery',
                          packaging_status: 'Out For Delivery',
                          event_location: ofdLocation,
                          event_remarks: ofdRemarks
                        })}
                      >
                        🚚 Dispatch Out For Delivery
                      </button>
                      <button
                        type="button"
                        className="btn-workflow-danger"
                        onClick={() => handleWorkflowSubmit({
                          status: 'cancelled'
                        })}
                      >
                        Cancel / Return Order
                      </button>
                    </div>
                  </div>
                )}

                {/* Stage 5: Delivery Confirmation (Shown for out_for_delivery orders) */}
                {order.status === 'out_for_delivery' && (
                  <div className="dispatch-form-group">
                    <span className="workflow-section-title">Stage 5: Delivery Confirmation</span>
                    
                    <div style={{ background: '#f8fafc', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0', marginTop: '8px', marginBottom: '12px' }}>
                      <div className="dispatch-form-group" style={{ marginBottom: '8px' }}>
                        <label style={{ fontSize: '12px', display: 'block', marginBottom: '3px' }}>Delivery Destination</label>
                        <input
                          type="text"
                          value={deliveredLocation}
                          onChange={(e) => setDeliveredLocation(e.target.value)}
                          placeholder="e.g. Customer Address"
                          style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                        />
                      </div>
                      <div className="dispatch-form-group" style={{ marginBottom: '8px' }}>
                        <label style={{ fontSize: '12px', display: 'block', marginBottom: '3px' }}>Delivery Remarks</label>
                        <input
                          type="text"
                          value={deliveredRemarks}
                          onChange={(e) => setDeliveredRemarks(e.target.value)}
                          placeholder="e.g. Successfully delivered"
                          style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                        />
                      </div>
                    </div>

                    <div className="workflow-buttons-row">
                      <button
                        type="button"
                        className="btn-workflow-primary"
                        onClick={() => handleWorkflowSubmit({
                          status: 'delivered',
                          packaging_status: 'Delivered',
                          event_location: deliveredLocation,
                          event_remarks: deliveredRemarks
                        })}
                      >
                        ✓ Mark as Delivered Successfully
                      </button>
                      <button
                        type="button"
                        className="btn-workflow-danger"
                        onClick={() => handleWorkflowSubmit({
                          status: 'cancelled'
                        })}
                      >
                        Cancel / Returned
                      </button>
                    </div>
                  </div>
                )}

                {/* Terminal statuses (delivered, cancelled, refunded) */}
                {['delivered', 'cancelled', 'refunded'].includes(order.status) && (
                  <p style={{ margin: 0, fontSize: '13px', color: '#64748b', fontStyle: 'italic' }}>
                    This order is in a terminal status: <strong>{order.status.toUpperCase()}</strong>. No further administrative transitions can be executed.
                  </p>
                )}
              </div>
            </div>

            {/* 6. Tracking Timeline Management */}
            {['shipped', 'out_for_delivery', 'delivered'].includes(order.status) && (
              <div className="order-details-glass-panel" style={{ marginTop: '20px' }}>
                <h3>Tracking Timeline Management</h3>
                
                {/* List current events */}
                <div style={{ marginBottom: '16px', maxHeight: '180px', overflowY: 'auto' }}>
                  <span className="info-item-label" style={{ fontWeight: 600, display: 'block', marginBottom: '6px' }}>Logged Tracking Events</span>
                  {order.tracking_events && order.tracking_events.length > 0 ? (
                    <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse', marginTop: '6px' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid #cbd5e1', textAlign: 'left', color: '#64748b' }}>
                          <th style={{ padding: '6px 4px' }}>Date</th>
                          <th style={{ padding: '6px 4px' }}>Event/Status</th>
                          <th style={{ padding: '6px 4px' }}>Location</th>
                          <th style={{ padding: '6px 4px' }}>Remarks</th>
                        </tr>
                      </thead>
                      <tbody>
                        {order.tracking_events.map((ev, idx) => (
                          <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                            <td style={{ padding: '6px 4px', whiteSpace: 'nowrap', color: '#64748b' }}>{new Date(ev.created_at).toLocaleDateString()}</td>
                            <td style={{ padding: '6px 4px', fontWeight: 600, color: '#0f172a' }}>{ev.status}</td>
                            <td style={{ padding: '6px 4px', color: '#334155' }}>{ev.location || '-'}</td>
                            <td style={{ padding: '6px 4px', color: '#64748b' }}>{ev.remarks || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p style={{ margin: '6px 0', fontSize: '12.5px', color: '#64748b', fontStyle: 'italic' }}>No tracking events logged yet.</p>
                  )}
                </div>

                {/* Add new event form */}
                <div style={{ background: '#f8fafc', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  <h4 style={{ fontSize: '13px', fontWeight: 600, color: '#0f172a', marginBottom: '8px' }}>Add Timeline Entry</h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                      <div>
                        <label style={{ fontSize: '11px', color: '#475569', display: 'block', marginBottom: '2px' }}>Event Status</label>
                        <select 
                          value={newEventStatus} 
                          onChange={(e) => setNewEventStatus(e.target.value)}
                          style={{ width: '100%', padding: '6px', fontSize: '12px', borderRadius: '4px', border: '1px solid #cbd5e1', background: '#fff' }}
                        >
                          <option value="Accepted at Post Office">Accepted at Post Office</option>
                          <option value="In Transit">In Transit</option>
                          <option value="Reached Destination City">Reached Destination City</option>
                          <option value="Out For Delivery">Out For Delivery</option>
                          <option value="Delivered">Delivered</option>
                          <option value="Custom">Custom Event...</option>
                        </select>
                      </div>
                      <div>
                        <label style={{ fontSize: '11px', color: '#475569', display: 'block', marginBottom: '2px' }}>Location</label>
                        <input 
                          type="text" 
                          value={newEventLocation} 
                          onChange={(e) => setNewEventLocation(e.target.value)}
                          placeholder="e.g. Imphal Sorting Hub"
                          style={{ width: '100%', padding: '6px', fontSize: '12px', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                        />
                      </div>
                    </div>
                    
                    {newEventStatus === 'Custom' && (
                      <div>
                        <label style={{ fontSize: '11px', color: '#475569', display: 'block', marginBottom: '2px' }}>Custom Status Name</label>
                        <input 
                          type="text" 
                          value={customEventStatus} 
                          onChange={(e) => setCustomEventStatus(e.target.value)}
                          placeholder="e.g. Returned to Sender"
                          style={{ width: '100%', padding: '6px', fontSize: '12px', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                        />
                      </div>
                    )}

                    <div>
                      <label style={{ fontSize: '11px', color: '#475569', display: 'block', marginBottom: '2px' }}>Remarks / Activity Log</label>
                      <input 
                        type="text" 
                        value={newEventRemarks} 
                        onChange={(e) => setNewEventRemarks(e.target.value)}
                        placeholder="e.g. Parcel reached transit sorting hub"
                        style={{ width: '100%', padding: '6px', fontSize: '12px', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                      />
                    </div>
                    
                    <button 
                      type="button" 
                      onClick={handleAddManualEvent}
                      className="admin-add-btn"
                      style={{ fontSize: '12px', padding: '6px 12px', borderRadius: '4px', width: 'auto', alignSelf: 'flex-end', background: '#00B207', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 600 }}
                    >
                      + Add Entry
                    </button>
                  </div>
                </div>
              </div>
            )}

          </div>

          {/* Right Column: Ordered Items */}
          <div className="order-details-col-right">
            <div className="order-details-glass-panel">
              <h3>Ordered Items ({order.items?.length || 0})</h3>
              <div className="order-items-grid" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {order.items?.map((item, idx) => (
                  <div key={idx} className="item-detail-row" style={{ display: 'flex', gap: '12px', alignItems: 'center', paddingBottom: '12px', borderBottom: '1px solid #f1f5f9' }}>
                    <img src={item.image || "/products/Super Novel.png"} alt={item.title} className="order-item-img-preview" style={{ width: '48px', height: '48px', objectFit: 'contain', borderRadius: '4px', background: '#f8fafc' }} />
                    <div className="item-metadata" style={{ flex: 1 }}>
                      <h4 style={{ margin: 0, fontSize: '13px', fontWeight: 600, color: '#0f172a' }}>{item.title}</h4>
                      <p style={{ margin: '2px 0 0 0', fontSize: '11px', color: '#64748b' }}>SKU: <code>{item.sku}</code></p>
                      <div className="item-calc" style={{ marginTop: '4px', fontSize: '12px' }}>
                        <strong>₹{parseFloat(item.price).toFixed(2)}</strong> x {item.quantity} units
                      </div>
                    </div>
                    <div className="item-total-col" style={{ fontSize: '13px', fontWeight: 600, color: '#0f172a' }}>
                      ₹{(parseFloat(item.price) * item.quantity).toFixed(2)}
                    </div>
                  </div>
                ))}
              </div>

              <div className="order-summary-box" style={{ marginTop: '20px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12.5px', color: '#475569' }}>
                  <span>Subtotal:</span>
                  <span>₹{(parseFloat(order.total_amount) - parseFloat(order.shipping_amount || 0) - parseFloat(order.tax_amount || 0)).toFixed(2)}</span>
                </div>
                {parseFloat(order.tax_amount) > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12.5px', color: '#475569' }}>
                    <span>Tax:</span>
                    <span>₹{parseFloat(order.tax_amount).toFixed(2)}</span>
                  </div>
                )}
                {parseFloat(order.shipping_amount) > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12.5px', color: '#475569' }}>
                    <span>Shipping Fee:</span>
                    <span>₹{parseFloat(order.shipping_amount).toFixed(2)}</span>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14.5px', fontWeight: 'bold', color: '#0f172a', borderTop: '1px solid #e2e8f0', paddingTop: '8px', marginTop: '4px' }}>
                  <span>Total Amount:</span>
                  <span>₹{parseFloat(order.total_amount).toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>

        </div>

        <footer className="form-action-footer" style={{ padding: '16px 24px', borderTop: '1px solid #e2e8f0' }}>
          <button className="btn-primary-submit" onClick={onClose}>Close Order Details</button>
        </footer>
      </div>
    </div>
  );
}
